import {
  Mapping,
  ModelConceptClass,
  ModelConceptRelationship as ModelConceptRelationshipLabel,
  RelationshipMapping,
} from 'src/containers/ProjectDiagram/types/data';
import {
  LinkData,
  Model,
  ModelSnapshot,
} from 'src/containers/ProjectDiagram/types/model';
import {
  isMapping,
  isRelationshipMapping,
} from 'src/containers/ProjectDiagram/utils/data';
import { getAllClassHierarchy } from 'src/containers/ProjectDiagram/utils/getAllClassHierarchy';
import { ClassHierarchy } from 'src/containers/ProjectDiagram/utils/getClassHierarchy';
import { isValidClassMapping } from 'src/containers/ProjectDiagram/utils/mapping/isValidMapping';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';

/**
 * Find all mappings for a ModelConceptClass.
 */
const mappingForClassFilter =
  (data: ModelConceptClass) =>
  (mapping: LinkData): mapping is Mapping =>
    isMapping(mapping) && mapping.to === data.key;

export const hasMappingsForClass =
  (data: ModelConceptClass) =>
  (model: Model | ModelSnapshot): boolean => {
    return model.linkDataArray.some(mappingForClassFilter(data));
  };

/**
 * Find all valid mappings for a ModelConceptClass.
 */
const validMappingForClassFilter =
  (data: ModelConceptClass) =>
  (mapping: LinkData): mapping is Mapping =>
    isMapping(mapping) &&
    mapping.to === data.key &&
    isValidClassMapping(mapping);

export const hasValidMappingsForClass =
  (data: ModelConceptClass) =>
  (model: Model | ModelSnapshot): boolean => {
    return model.linkDataArray.some(validMappingForClassFilter(data));
  };

const buildIsMappingClassEqual =
  (mappingsByKey: Record<string, Mapping>) =>
  (mappingKey: string, classKey?: string): boolean => {
    const childClassIri = getModelConceptClassValue(
      mappingsByKey[mappingKey]?.to || ''
    );
    const parentClassIri = getModelConceptClassValue(classKey || '');
    if (!childClassIri || !parentClassIri) {
      return false;
    }
    if (childClassIri === parentClassIri) {
      return true;
    }

    return false;
  };

const buildIsMappingClassDescendant =
  (
    hierarchyByIri: Record<string, ClassHierarchy>,
    mappingsByKey: Record<string, Mapping>
  ) =>
  (mappingKey: string, classKey?: string): boolean => {
    const childClassIri = getModelConceptClassValue(
      mappingsByKey[mappingKey]?.to || ''
    );
    const parentClassIri = getModelConceptClassValue(classKey || '');
    if (!childClassIri || !parentClassIri) {
      return false;
    }
    if (childClassIri === parentClassIri) {
      return true;
    }

    return Boolean(hierarchyByIri[parentClassIri]?.descendants[childClassIri]);
  };

/**
 * Find all mappings for a ModelConceptRelationshipLabel.
 */
const mappingsForRelationshipFilter =
  (
    data: ModelConceptRelationshipLabel,
    doesMappingClassMatch: (mappingKey: string, classKey?: string) => boolean
  ) =>
  (mapping: RelationshipMapping) =>
    Boolean(
      (mapping.from && doesMappingClassMatch(mapping.from, data.from)) ||
        (mapping.to && doesMappingClassMatch(mapping.to, data.to))
    );

export const hasMappingsForRelationship =
  (data: ModelConceptRelationshipLabel) =>
  (model: Model | ModelSnapshot): boolean => {
    const fromIri = data.from && getModelConceptClassValue(data.from);
    const toIri = data.to && getModelConceptClassValue(data.to);

    // ignore relationships that are unconnected
    if (!fromIri || !toIri) {
      return false;
    }

    const mappingsByKey: Record<string, Mapping> = {};
    const relationshipMappings: RelationshipMapping[] = [];

    model.linkDataArray.forEach((linkData) => {
      if (isMapping(linkData)) {
        mappingsByKey[linkData.key] = linkData;
      } else if (
        isRelationshipMapping(linkData) &&
        linkData.relationshipIri === data.value &&
        (linkData.toClassIri === toIri || linkData.fromClassIri === fromIri)
      ) {
        relationshipMappings.push(linkData);
      }
    });

    // ignore if there are no relationship mappings
    // ignore if there are no class mappings (relationship mappings require a class mapping)
    if (!relationshipMappings.length || !Object.keys(mappingsByKey).length) {
      return false;
    }

    // check against direct classes first to avoid computing hierarchy
    const hasDirectMapping = relationshipMappings.some(
      mappingsForRelationshipFilter(
        data,
        buildIsMappingClassEqual(mappingsByKey)
      )
    );
    if (hasDirectMapping) {
      return true;
    }

    // only build hierarchy if necessary
    return relationshipMappings.some(
      mappingsForRelationshipFilter(
        data,
        buildIsMappingClassDescendant(
          getAllClassHierarchy(model),
          mappingsByKey
        )
      )
    );
  };

export const hasValidMappingsForRelationship =
  (data: ModelConceptRelationshipLabel) =>
  (model: Model | ModelSnapshot): boolean => {
    const fromIri = data.from && getModelConceptClassValue(data.from);
    const toIri = data.to && getModelConceptClassValue(data.to);

    // ignore relationships that are unconnected
    if (!fromIri || !toIri) {
      return false;
    }

    const mappingsByKey: Record<string, Mapping> = {};
    const relationshipMappings: RelationshipMapping[] = [];

    model.linkDataArray.forEach((linkData) => {
      if (isMapping(linkData)) {
        mappingsByKey[linkData.key] = linkData;
      } else if (
        isRelationshipMapping(linkData) &&
        linkData.relationshipIri === data.value &&
        (linkData.toClassIri === toIri || linkData.fromClassIri === fromIri) &&
        (linkData.toPrimaryIdentifier?.parts.length ||
          linkData.fromPrimaryIdentifier?.parts.length)
      ) {
        relationshipMappings.push(linkData);
      }
    });

    // ignore if there are no relationship mappings
    // ignore if there are no class mappings (relationship mappings require a class mapping)
    if (!relationshipMappings.length || !Object.keys(mappingsByKey).length) {
      return false;
    }

    // check against direct classes first to avoid computing hierarchy
    const hasDirectMapping = relationshipMappings.some(
      mappingsForRelationshipFilter(
        data,
        buildIsMappingClassEqual(mappingsByKey)
      )
    );
    if (hasDirectMapping) {
      return true;
    }

    // only build hierarchy if necessary
    return relationshipMappings.some(
      mappingsForRelationshipFilter(
        data,
        buildIsMappingClassDescendant(
          getAllClassHierarchy(model),
          mappingsByKey
        )
      )
    );
  };
